/**
 * MayoExpress icon library — fuente única de todos los SVG del sistema.
 * stroke-based, fill: none, currentColor.
 * Tamaños: 14 (tabla), 16 (nav/form), 18 (close/modal), 20 (empty state).
 * strokeWidth: 1.6 por defecto, 1.8 en contextos pequeños/densos.
 */

interface IconProps {
  size?: number
  strokeWidth?: number
  className?: string
  style?: React.CSSProperties
}

function Svg({ size = 16, strokeWidth = 1.6, children, className, style }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      {children}
    </svg>
  )
}

// ─── Acciones CRUD ───────────────────────────────────────────────────────────

export function PlusIcon(p: IconProps) {
  return <Svg {...p}><path d="M12 5v14M5 12h14" /></Svg>
}

export function EditIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </Svg>
  )
}

export function TrashIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </Svg>
  )
}

export function CloseIcon(p: IconProps) {
  return <Svg {...p}><path d="M18 6L6 18M6 6l12 12" /></Svg>
}

// ─── Visibilidad ─────────────────────────────────────────────────────────────

export function EyeIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  )
}

export function EyeOffIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <path d="M1 1l22 22" />
    </Svg>
  )
}

// ─── Navegación ──────────────────────────────────────────────────────────────

export function SearchIcon(p: IconProps) {
  return <Svg {...p}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></Svg>
}

export function ChevronDownIcon(p: IconProps) {
  return <Svg {...p}><polyline points="6 9 12 15 18 9" /></Svg>
}

export function ChevronRightIcon(p: IconProps) {
  return <Svg {...p}><polyline points="9 18 15 12 9 6" /></Svg>
}

export function ArrowLeftIcon(p: IconProps) {
  return <Svg {...p}><path d="M19 12H5M12 5l-7 7 7 7" /></Svg>
}

// ─── Estado / feedback ───────────────────────────────────────────────────────

export function CheckIcon(p: IconProps) {
  return <Svg {...p}><polyline points="20 6 9 17 4 12" /></Svg>
}

export function AlertTriangleIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </Svg>
  )
}

export function InfoIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </Svg>
  )
}

// ─── Negocio ─────────────────────────────────────────────────────────────────

export function BoxIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 8l8-4 8 4v8l-8 4-8-4V8z" />
      <path d="M4 8l8 4 8-4M12 12v8" />
    </Svg>
  )
}

export function TagIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <circle cx="7" cy="7" r="1.5" />
    </Svg>
  )
}

export function WarehouseIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2" y="9" width="20" height="12" rx="1" />
      <path d="M12 3L2 9h20L12 3z" />
      <path d="M10 21v-6h4v6" />
    </Svg>
  )
}

export function TrendUpIcon(p: IconProps) {
  return <Svg {...p}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></Svg>
}

export function HistoryIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <polyline points="12 8 12 12 14 14" />
      <path d="M3.05 11a9 9 0 1 0 .5-4H1" />
      <polyline points="1 3 1 7 5 7" />
    </Svg>
  )
}

// ─── UI ──────────────────────────────────────────────────────────────────────

export function FilterIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </Svg>
  )
}

export function LogoutIcon(p: IconProps) {
  return <Svg {...p}><path d="M14 4h5v16h-5M9 8l-4 4 4 4M5 12h11" /></Svg>
}

export function SettingsIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </Svg>
  )
}

export function UsersIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="9" cy="7" r="3" />
      <path d="M3 20c1-4 12-4 12 0" />
      <circle cx="18" cy="8" r="2.5" />
      <path d="M20 20c.5-2 4-2 4 0" />
    </Svg>
  )
}

export function MailIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </Svg>
  )
}

export function LockIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </Svg>
  )
}

export function MinusIcon(p: IconProps) {
  return <Svg {...p}><path d="M5 12h14" /></Svg>
}

export function PrintIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6 9V3h12v6" />
      <rect x="3" y="9" width="18" height="9" rx="2" />
      <path d="M6 14h12M6 18h4" />
      <circle cx="18" cy="13" r="0.5" fill="currentColor" />
    </Svg>
  )
}

export function CameraIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
      <circle cx="12" cy="13" r="4" />
    </Svg>
  )
}
